import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import { logoutWithDelay } from 'store/oauth/service';
import { PatientStatus } from 'constants/enums';
import { glucoseClasses } from 'constants/constants';

import {
  getPatient,
  getPatientDevices,
  getMeasurement,
  getEgvs,
  getRefRange,
  getBaselines
} from './service';

export const initialState: IPatientState = {
  data: null,
  baselines: {
    data: [],
    loading: false,
    error: false,
    uid: null,
    requestedUid: null
  },
  devices: {
    data: [],
    loading: false,
    error: false
  },
  loading: false,
  error: false,
  errorMessage: '',
  information: {
    search: ''
  },
  inProgress: {
    note: '',
    message: '',
    filesInBox: [],
    questionnaireGraphPeriod: '7d',
    questionnaireGraphRange: {
      startDate: DateTime.now().minus({ days: 6 }).startOf('day'),
      endDate: DateTime.now().endOf('day')
    },
    survey: null
  },
  vitals: {
    loading: false,
    error: false,
    view: 'line',
    viewTempOverride: null,
    data: null,
    dexcom: [],
    dexcomError: false,
    refRange: [],
    measurementGraphPeriod: '14d',
    measurementGraphRange: {
      startDate: DateTime.now().minus({ days: 13 }).startOf('day'),
      endDate: DateTime.now().endOf('day')
    },
    minMaxYAxis: {
      min: 0,
      max: 0
    },
    measurementGraphStats: [],
    selectedMeasurementClasses: []
  },
  audioCallInProgress: false,
  videoCallInProgress: false
};

const slice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    clearPatient: () => initialState,
    updateAssignedTo: (state, action: PayloadAction<ISelectOption[]>) => {
      if (state.data?.assignedTo) state.data.assignedTo = action.payload;
    },
    updatePatientStatus: (state, action: PayloadAction<string>) => {
      if (state.data?.patientStatus) state.data.patientStatus = action.payload;
    },
    updateTags: (state, action: PayloadAction<ISelectOption[]>) => {
      if (state.data) state.data.tags = action.payload;
    },
    updatePatient: (state, action: PayloadAction<any>) => {
      state.data = { ...state.data, ...action.payload };
    },
    updateLogType: (
      state,
      action: PayloadAction<{
        logType: string;
        time: number;
        oldValues?: { logType: string; time: number } | null;
      }>
    ) => {
      const timeToAdd = action.payload.oldValues?.time
        ? action.payload.time - action.payload.oldValues.time
        : action.payload.time;
      if (state.data) {
        const logType = state.data.logTypes?.[action.payload.logType] || 0;
        const oldLogType = action.payload.oldValues
          ? state.data.logTypes?.[action.payload.oldValues?.logType] || 0
          : 0;
        state.data.logTypes = {
          ...state.data?.logTypes,
          ...(action.payload.oldValues && {
            [action.payload.oldValues.logType]: oldLogType - action.payload.oldValues.time
          }),
          [action.payload.logType]: logType + timeToAdd
        };
      }
    },
    updateDevices: (state, action: PayloadAction<IDeviceListItem[]>) => {
      state.devices.data = action.payload;
    },
    setReportView: (state, action: PayloadAction<IAmbulatoryVitalsReport | undefined>) => {
      state.vitals.report = action.payload;
    },
    setNote: (state, action: PayloadAction<string>) => {
      state.inProgress.note = action.payload;
    },
    setMessage: (state, action: PayloadAction<string>) => {
      state.inProgress.message = action.payload;
    },
    setFilesInBox: (state, action: PayloadAction<{ type: string; src: any }[]>) => {
      state.inProgress.filesInBox = action.payload;
    },
    setMeasurementGraphPeriod: (state, action: PayloadAction<IPeriod>) => {
      state.vitals.measurementGraphPeriod = action.payload;
    },
    setMeasurementGraphRange: (
      state,
      action: PayloadAction<{ startDate: DateTime; endDate: DateTime }>
    ) => {
      state.vitals.measurementGraphRange = {
        startDate: action.payload.startDate.startOf('day'),
        endDate: action.payload.endDate.endOf('day')
      };
    },
    setMeasurementGraphStats: (state, action: PayloadAction<IGraphStats[]>) => {
      state.vitals.measurementGraphStats = action.payload;
    },
    setSelectedMeasurementClasses: (state, action: PayloadAction<IMeasurementClassType[]>) => {
      state.vitals.selectedMeasurementClasses = action.payload;
      const hasGlucoseClasses = action.payload.some((clazz: IMeasurementClassType) =>
        glucoseClasses.includes(clazz)
      );
      if (!hasGlucoseClasses) {
        state.vitals.viewTempOverride = null;
      }
    },
    setQuestionnaireGraphPeriod: (state, action: PayloadAction<IPeriod>) => {
      state.inProgress.questionnaireGraphPeriod = action.payload;
    },
    setQuestionnaireGraphRange: (
      state,
      action: PayloadAction<{ startDate: DateTime; endDate: DateTime }>
    ) => {
      state.inProgress.questionnaireGraphRange = action.payload;
    },
    setSurvey: (state, action: PayloadAction<ISurvey | null>) => {
      state.inProgress.survey = action.payload;
    },
    setSurveyJs: (state, action: PayloadAction<any>) => {
      state.inProgress.surveyjs = action.payload;
    },
    clearSurveyJs: (state) => {
      state.inProgress.surveyjs = undefined;
    },
    setVitalsView: (state, action: PayloadAction<IGraphType>) => {
      state.vitals.view = action.payload;
      state.vitals.viewTempOverride = null;
    },
    setVitalsViewTempOverride: (state, action: PayloadAction<IGraphType>) => {
      state.vitals.viewTempOverride = action.payload;
    },
    setVitalsData: (state, action: PayloadAction<IMeasurementResponse>) => {
      state.vitals.data = action.payload;
    },
    setRefRange: (state, action: PayloadAction<ICarePlanReferenceRange[]>) => {
      state.vitals.refRange = action.payload;
    },
    clearVitalsData: (state) => {
      state.vitals.data = null;
      state.vitals.dexcom = [];
      state.vitals.refRange = [];
    },
    setMinMaxYAxis: (state, action: PayloadAction<{ min: number; max: number }>) => {
      state.vitals.minMaxYAxis = action.payload;
    },
    setAudioCallInProgress: (state, action: PayloadAction<boolean>) => {
      state.audioCallInProgress = action.payload;
    },
    setVideoCallInProgress: (state, action: PayloadAction<boolean>) => {
      state.videoCallInProgress = action.payload;
    },
    updateInformationSearch: (state, action: PayloadAction<string>) => {
      state.information.search = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPatient.pending, (state, action) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(getPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.inProgress.note = '';
        state.inProgress.message = '';
        state.data = action.payload;
      })
      .addCase(getPatient.rejected, (state, action) => {
        state.loading = false;
        state.error = true;
        state.errorMessage = action.error?.message || '';
      })
      .addCase(getPatientDevices.pending, (state, action) => {
        state.devices.loading = true;
        state.devices.error = false;
      })
      .addCase(getPatientDevices.fulfilled, (state, action) => {
        state.devices.loading = false;
        state.devices.data = action.payload;
      })
      .addCase(getPatientDevices.rejected, (state, action) => {
        state.devices.loading = false;
        state.devices.error = true;
      })
      .addCase(getMeasurement.pending, (state, action) => {
        state.vitals.data = null;
        state.vitals.error = false;
      })
      .addCase(getMeasurement.rejected, (state, action) => {
        state.vitals.error = true;
      })
      .addCase(getEgvs.pending, (state, action) => {
        state.vitals.dexcom = [];
        state.vitals.loading = true;
        state.vitals.dexcomError = false;
      })
      .addCase(getEgvs.fulfilled, (state, action) => {
        state.vitals.dexcom = action.payload;
        state.vitals.loading = false;
      })
      .addCase(getEgvs.rejected, (state, action) => {
        state.vitals.dexcomError = true;
        state.vitals.loading = false;
      })
      .addCase(getRefRange.pending, (state, action) => {
        state.vitals.refRange = [];
        state.vitals.error = false;
      })
      .addCase(getRefRange.fulfilled, (state, action) => {
        state.vitals.refRange = action.payload;
      })
      .addCase(getRefRange.rejected, (state, action) => {
        state.vitals.error = true;
      })
      .addCase(getBaselines.pending, (state, action) => {
        if (state.baselines.uid !== action.meta.arg) {
          state.baselines.data = [];
          state.baselines.uid = null;
        }
        state.baselines.requestedUid = action.meta.arg;
        state.baselines.loading = true;
        state.baselines.error = false;
      })
      .addCase(getBaselines.fulfilled, (state, action) => {
        if (state.baselines.requestedUid !== action.meta.arg) return;
        state.baselines.loading = false;
        state.baselines.error = false;
        state.baselines.uid = action.meta.arg;
        state.baselines.data = action.payload;
      })
      .addCase(getBaselines.rejected, (state, action) => {
        if (state.baselines.requestedUid !== action.meta.arg) return;
        state.baselines.loading = false;
        state.baselines.error = true;
        state.baselines.uid = null;
        state.baselines.data = [];
      })
      .addCase(logoutWithDelay.fulfilled, () => initialState);
  }
});

const getPatientCondition = (state: IPatient, condition: string) => {
  const obj = state.conditions?.find((item) => item[condition]);
  return obj ? obj[condition] : '';
};

const getPatientData = (state: IAppState) => {
  return state.patient.data;
};

const noBaselines: IBaseline[] = [];

const selectPatientBaselines = (state: IAppState) => {
  const { data, uid } = state.patient.baselines;
  return uid && uid === state.patient.data?.uid ? data : noBaselines;
};

const isPatientArchived = (state: IAppState) => {
  return state.patient.data?.patientStatus === PatientStatus.ARCHIVED;
};

const selectPatientAudioCallInProgress = (state: IAppState) => {
  return state.patient.audioCallInProgress;
};

const selectPatientVideoCallInProgress = (state: IAppState) => {
  return state.patient.videoCallInProgress;
};

const selectPatientMobilePhoneNumber = (state: IAppState) => {
  return state.patient.data?.MobilePhoneNumber;
};

const hasEHRIntegration = (state: IAppState) => {
  return state.patient.data?.EHRIntegrationType === 2;
};

const selectPatientUid = (state: IAppState) => state.patient.data?.uid;

const selectVitalsView = (state: IAppState) => {
  return state.patient.vitals.viewTempOverride || state.patient.vitals.view;
};

export {
  getPatientCondition,
  getPatientData,
  isPatientArchived,
  selectPatientBaselines,
  selectPatientAudioCallInProgress,
  selectPatientVideoCallInProgress,
  selectPatientMobilePhoneNumber,
  hasEHRIntegration,
  selectPatientUid,
  selectVitalsView
};

export const {
  clearPatient,
  updateAssignedTo,
  updatePatientStatus,
  updatePatient,
  updateTags,
  setNote,
  setMessage,
  setFilesInBox,
  updateDevices,
  updateLogType,
  setMeasurementGraphPeriod,
  setMeasurementGraphRange,
  setMeasurementGraphStats,
  setSelectedMeasurementClasses,
  setQuestionnaireGraphPeriod,
  setQuestionnaireGraphRange,
  setSurvey,
  setSurveyJs,
  clearSurveyJs,
  setVitalsView,
  setVitalsViewTempOverride,
  setVitalsData,
  setRefRange,
  clearVitalsData,
  setMinMaxYAxis,
  setReportView,
  setAudioCallInProgress,
  setVideoCallInProgress,
  updateInformationSearch
} = slice.actions;
export const patientReducer = slice.reducer;
